import dayjs, { Dayjs } from "dayjs";
import type { ReportItemKind } from "./reportItemKinds";

export type FindingStatus = "pending" | "draft" | "completed";
export type FindingStatusFilter = FindingStatus | "overdue";

// The fields of a finding or follow-up action these helpers read.
export type FindingLike = {
	status?: string;
	already_completed?: boolean;
	deadline?: string | Date | null;
	completed_on?: string | Date | null;
	// Lower-cased email of whoever completed it, where that is recorded.
	completed_by?: string | null;
	completed_by_name?: string;
	responsible?: string;
	responsible_name?: string;
	finding_type?: string;
	findings_description?: string;
	recommended_actions?: string;
	what_was_done?: string;
	// A follow-up action's names for its type, text and outcome.
	action_type?: string;
	description?: string;
	closing_comment?: string;
};

export type FindingFilters = {
	search: string;
	statuses: FindingStatusFilter[];
	// Lower-cased emails.
	responsible: string[];
	types: string[];
};

export const EMPTY_FINDING_FILTERS: FindingFilters = {
	search: "",
	statuses: [],
	responsible: [],
	types: [],
};

// Shared by the status tags on each finding and the status filter.
export const FINDING_STATUS_LABELS: Record<FindingStatus, string> = {
	pending: "New",
	draft: "In process",
	completed: "Completed",
};

export const FINDING_STATUS_FILTER_OPTIONS: {
	label: string;
	value: FindingStatusFilter;
}[] = [
	{ label: FINDING_STATUS_LABELS.pending, value: "pending" },
	{ label: FINDING_STATUS_LABELS.draft, value: "draft" },
	{ label: FINDING_STATUS_LABELS.completed, value: "completed" },
	{ label: "Overdue", value: "overdue" },
];

export const getFindingLabel = (index: number) => `#Findings ${index + 1}`;

// How the audit's page names a finding.
export const getFindingTitle = (index: number) => `Finding ${index + 1}`;

// The status chips on the audit's page. Each one sets the status filter; Open
// is New or In process, overdue or not.
export type FindingStatusChip = "all" | "open" | "overdue" | "completed";

export const FINDING_STATUS_CHIPS: {
	key: FindingStatusChip;
	label: string;
	statuses: FindingStatusFilter[];
}[] = [
	{ key: "all", label: "All", statuses: [] },
	{ key: "open", label: "Open", statuses: ["pending", "draft"] },
	{ key: "overdue", label: "Overdue", statuses: ["overdue"] },
	{ key: "completed", label: "Completed", statuses: ["completed"] },
];

const statusesKey = (statuses: FindingStatusFilter[]) =>
	[...statuses].sort((a, b) => a.localeCompare(b)).join(",");

// The chip that sets exactly these statuses, if any.
export function getActiveStatusChip(statuses: FindingStatusFilter[]) {
	const key = statusesKey(statuses);
	return FINDING_STATUS_CHIPS.find((chip) => statusesKey(chip.statuses) === key)
		?.key;
}

// Findings only carry a workflow status, shown as a tag, once the audit is out
// for action.
export function areFindingStatusesShown(openStatus?: string | null) {
	return ["in_progress", "revision_required", "final_review"].includes(
		openStatus ?? ""
	);
}

// The audit's page shows statuses, progress and deadlines from the moment the
// audit goes out for action, and keeps them once it is approved to close or
// closed, unless it was closed as rejected. A status change from Control
// Centre keeps the old open_status, so that only counts while the audit is
// open.
export function areFindingOutcomesShown(
	reportStatus?: string | null,
	openStatus?: string | null,
	isRejected?: boolean | null
) {
	const status = reportStatus?.toLowerCase();
	if (status === "closed") return !isRejected;
	return (
		status === "open" &&
		(areFindingStatusesShown(openStatus) || openStatus === "ready_to_close")
	);
}

// Once an audit is approved to close, or closed, its findings' deadlines are
// no longer live.
export function isReportFinished(
	reportStatus?: string | null,
	openStatus?: string | null
) {
	const status = reportStatus?.toLowerCase();
	return (
		status === "closed" ||
		(status === "open" && openStatus === "ready_to_close")
	);
}

export function getFindingStatus(finding?: FindingLike): FindingStatus {
	if (finding?.already_completed === true) return "completed";
	const status = finding?.status?.toLowerCase?.();
	if (status === "completed" || status === "draft") return status;
	return "pending";
}

export function isFindingOverdue(
	finding?: FindingLike,
	today: Dayjs = dayjs()
) {
	if (!finding?.deadline || getFindingStatus(finding) === "completed") {
		return false;
	}
	const deadline = dayjs(finding.deadline);
	return deadline.isValid() && deadline.isBefore(today, "day");
}

export type FindingStatusCounts = {
	total: number;
	completed: number;
	// Every finding not completed, overdue or not.
	open: number;
	overdue: number;
	// In process and New leave out the overdue ones, so completed, overdue,
	// draft and pending add up to total.
	draft: number;
	pending: number;
};

// With deadlinesLive off, as once the audit is finished, nothing is overdue.
export function getFindingStatusCounts(
	findings: (FindingLike | undefined)[],
	today: Dayjs = dayjs(),
	deadlinesLive = true
): FindingStatusCounts {
	const counts: FindingStatusCounts = {
		total: findings.length,
		completed: 0,
		open: 0,
		overdue: 0,
		draft: 0,
		pending: 0,
	};
	for (const finding of findings) {
		const status = getFindingStatus(finding);
		if (status === "completed") {
			counts.completed++;
			continue;
		}
		counts.open++;
		if (deadlinesLive && isFindingOverdue(finding, today)) counts.overdue++;
		else counts[status]++;
	}
	return counts;
}

export type FindingTiming =
	| { kind: "overdue"; days: number }
	| { kind: "due"; days: number }
	// completedOn is null without a valid completed_on date; lateDays is also
	// null without a valid deadline.
	| { kind: "done"; completedOn: Dayjs | null; lateDays: number | null }
	| { kind: "none" };

const toValidDate = (value?: string | Date | null) => {
	if (!value) return null;
	const date = dayjs(value);
	return date.isValid() ? date : null;
};

// When a finding was completed, if it records a valid date.
export const getFindingCompletedOn = (finding?: FindingLike) =>
	toValidDate(finding?.completed_on);

// How a finding stands against its deadline, in whole days. One completed
// during the audit has no deadline to meet. With deadlinesLive off, as once
// the audit is finished, an open finding has none either.
export function getFindingTiming(
	finding: FindingLike | undefined,
	today: Dayjs = dayjs(),
	deadlinesLive = true
): FindingTiming {
	if (!finding || finding.already_completed === true) return { kind: "none" };
	const deadline = toValidDate(finding.deadline);

	if (getFindingStatus(finding) === "completed") {
		const completedOn = getFindingCompletedOn(finding);
		const lateDays =
			completedOn && deadline
				? Math.max(
						0,
						completedOn.startOf("day").diff(deadline.startOf("day"), "day")
					)
				: null;
		return { kind: "done", completedOn, lateDays };
	}

	if (!deadline || !deadlinesLive) return { kind: "none" };
	const days = deadline.startOf("day").diff(today.startOf("day"), "day");
	return days < 0 ? { kind: "overdue", days: -days } : { kind: "due", days };
}

export const formatDayCount = (days: number) =>
	`${days} day${days === 1 ? "" : "s"}`;

export type FindingPersonInfo = {
	label: "Completed by" | "Responsible";
	email: string;
	name: string;
};

// The person a finding shows: whoever completed it, once it is completed and
// that is recorded, otherwise whoever is responsible for it.
export function getFindingPerson(finding?: FindingLike): FindingPersonInfo {
	// Only text counts: nothing checks what else could be stored there.
	const completedBy =
		typeof finding?.completed_by === "string" ? finding.completed_by : "";
	if (getFindingStatus(finding) === "completed" && completedBy) {
		return {
			label: "Completed by",
			email: completedBy,
			name: finding?.completed_by_name || completedBy, // NOSONAR: an empty stored name falls back to the email
		};
	}
	const email = finding?.responsible ?? "";
	return {
		label: "Responsible",
		email,
		name: finding?.responsible_name || email, // NOSONAR: an empty stored name falls back to the email
	};
}

// The report fields an item's stages read.
export type FindingStageReport = {
	status?: string;
	open_status?: string;
	is_rejected?: boolean;
	created_at?: string | Date | null;
	created_on?: string | Date | null;
	submitted_on?: string | Date | null;
	approved_on?: string | Date | null;
	reviewed_on?: string | Date | null;
};

export type FindingStageState = "done" | "current" | "todo";

export type FindingStage = {
	key: "created" | "reviewed" | "completed";
	state: FindingStageState;
	// A date and a note only on a done stage.
	date?: Dayjs;
	note?: { text: string; modifier?: "late" };
};

const doneStage = (
	key: FindingStage["key"],
	date: Dayjs | null,
	note?: FindingStage["note"]
): FindingStage => ({
	key,
	state: "done",
	...(date && { date }),
	...(note && { note }),
});

// How a completed item stood against its deadline. Worked out from the item
// itself, as the page blanks its timing while statuses are hidden.
const getCompletionNote = (
	finding: FindingLike | undefined,
	kind: ReportItemKind
): FindingStage["note"] | undefined => {
	if (finding?.already_completed === true) {
		return { text: kind.alreadyCompletedNote };
	}
	const timing = getFindingTiming(finding);
	if (timing.kind !== "done" || timing.lateDays === null) return undefined;
	return timing.lateDays > 0
		? { text: `${formatDayCount(timing.lateDays)} late`, modifier: "late" }
		: { text: "On time" };
};

// An item's stages: created with its report, reviewed when the report passes
// its initial review, then completed. Created and Reviewed are the report's,
// so every item in it shares them.
export function getFindingStages(
	finding: FindingLike | undefined,
	report: FindingStageReport,
	kind: ReportItemKind
): FindingStage[] {
	const isOpen = report.status?.toLowerCase() === "open";
	const isClosed = report.status?.toLowerCase() === "closed";
	// Past the initial review: out for action, approved to close, or closed.
	// Without a rejection flag, a closed report counts, rejected or not.
	const isReviewed = areFindingOutcomesShown(report.status, report.open_status);

	// When the draft was first saved. Reports written without it fall back to
	// created_on, then to when they were submitted, which a resubmission moves.
	const createdOn =
		toValidDate(report.created_at) ??
		toValidDate(report.created_on) ??
		toValidDate(report.submitted_on);
	// approved_on holds the initial approval, except on an audit, whose final
	// approval overwrites it on closing.
	const reviewedOn =
		toValidDate(report.reviewed_on) ??
		(isClosed && kind.finalApprovalOverwritesApprovedOn
			? null
			: toValidDate(report.approved_on));

	let reviewed: FindingStage = { key: "reviewed", state: "todo" };
	if (isReviewed) reviewed = doneStage("reviewed", reviewedOn);
	else if (isOpen && report.open_status === "in_review") {
		reviewed = { key: "reviewed", state: "current" };
	}

	let completed: FindingStage = { key: "completed", state: "todo" };
	if (getFindingStatus(finding) === "completed") {
		completed = doneStage(
			"completed",
			getFindingCompletedOn(finding),
			getCompletionNote(finding, kind)
		);
	} else if (
		isReviewed &&
		!isReportFinished(report.status, report.open_status)
	) {
		completed = { key: "completed", state: "current" };
	}

	return [doneStage("created", createdOn), reviewed, completed];
}

export function hasActiveFindingFilters(filters: FindingFilters) {
	return (
		filters.search.trim() !== "" ||
		filters.statuses.length > 0 ||
		filters.responsible.length > 0 ||
		filters.types.length > 0
	);
}

// Filters combine with AND; the values within one filter combine with OR.
export function matchesFindingFilters(
	finding: FindingLike | undefined,
	index: number,
	filters: FindingFilters,
	kind: ReportItemKind,
	today: Dayjs = dayjs()
) {
	const { statuses, responsible, types } = filters;

	if (
		statuses.length > 0 &&
		!statuses.includes(getFindingStatus(finding)) &&
		!(statuses.includes("overdue") && isFindingOverdue(finding, today))
	) {
		return false;
	}

	if (
		responsible.length > 0 &&
		!responsible.includes(finding?.responsible?.toLowerCase?.() ?? "")
	) {
		return false;
	}

	if (types.length > 0 && !types.includes(finding?.[kind.typeField] ?? "")) {
		return false;
	}

	const query = filters.search.trim().toLowerCase();
	if (!query) return true;
	return [
		kind.getDrawerLabel(index),
		kind.getTitle(index),
		finding?.[kind.descriptionField],
		kind.recommendedActionField && finding?.[kind.recommendedActionField],
		finding?.[kind.outcomeField],
	].some(
		(text) => typeof text === "string" && text.toLowerCase().includes(query)
	);
}

// Facts about all of an audit's findings that each finding's form needs: the
// last unfinished finding asks for confirmation before completing, and "What
// was done" shows once all are completed or to anyone responsible for one.
export function getFindingsProgress(
	findings: (FindingLike | undefined)[],
	currentUserEmail?: string
) {
	const email = currentUserEmail?.toLowerCase();
	return {
		notCompletedCount: findings.filter(
			(finding) =>
				!finding?.already_completed &&
				finding?.status?.toLowerCase?.() !== "completed"
		).length,
		allCompleted:
			findings.length > 0 &&
			findings.every((finding) => getFindingStatus(finding) === "completed"),
		currentUserIsAnyResponsible: findings.some(
			(finding) => finding?.responsible?.toLowerCase?.() === email
		),
	};
}

// One-line summary shown in a collapsed finding's header.
export function getFindingPreview(
	finding?: FindingLike,
	responsibleName?: string
) {
	const description = finding?.findings_description
		?.replace(/\s+/g, " ")
		.trim();
	return [finding?.finding_type, responsibleName, description]
		.filter(Boolean)
		.join(" · ");
}

// How an item's type reads: findings store theirs as shown, actions in
// lower case. Only text is formatted: nothing checks what else is stored.
export const getFindingTypeLabel = (type: string, kind: ReportItemKind) =>
	typeof type === "string" && kind.formatType ? kind.formatType(type) : type;

export function getFindingTypeOptions(
	findings: (FindingLike | undefined)[],
	kind: ReportItemKind
) {
	const types = new Set<string>(
		findings
			.map((finding) => finding?.[kind.typeField])
			// Only text: nothing checks what else could be stored there.
			.filter((type): type is string => typeof type === "string" && type !== "")
	);
	// The order a plain sort() gives, by UTF-16 code unit: localeCompare would
	// reorder types that differ only in case or accents.
	return [...types]
		.sort((a, b) => Number(a > b) - Number(a < b))
		.map((type) => ({ label: getFindingTypeLabel(type, kind), value: type }));
}

// The current user is listed first, as "Me".
export function getFindingResponsibleOptions(
	emails: string[],
	nameByEmail: Record<string, string>,
	currentUserEmail?: string
) {
	const me = currentUserEmail?.toLowerCase();
	const options = emails
		.filter((email) => email !== me)
		.map((email) => ({ label: nameByEmail[email] ?? email, value: email }))
		.sort((a, b) => a.label.localeCompare(b.label));
	return me && emails.includes(me)
		? [{ label: "Me", value: me }, ...options]
		: options;
}
